/**
 * 认证相关类型定义
 * 注意：这些类型是前端独立定义的，不导入后端实体
 */

// 用户账户类型枚举
export enum UserAccountType {
  PERSONAL_FREE = '个人免费',
  PERSONAL_PAID = '个人付费',
  ORGANIZATION_PAID = '组织付费'
}

// 用户订阅类型枚举
export enum UserSubscriptionType {
  UNSUBSCRIBED = '未订阅',
  PERSONAL_FREE = '个人免费版',
  PERSONAL_PRO = '个人专业版',
  PERSONAL_FLAGSHIP = '个人旗舰版',
  TEAM_PRO = '团队专业版',
  TEAM_FLAGSHIP = '团队旗舰版'
}

// 用户角色枚举
export enum UserRole {
  NORMAL_USER = '普通用户',
  SYSTEM_ADMIN = '系统管理员',
  ORG_LEADER = '组织负责人',
  DEPT_LEADER = '部门负责人'
}

// 用户语言枚举
export enum UserLanguage {
  ZH_CN = '中文简体',
  ZH_TW = '中文繁体',
  EN = '英语',
  JA = '日语',
  KO = '韩语'
}

// 用户货币枚举
export enum UserCurrency {
  CNY = '人民币',
  HKD = '港币',
  TWD = '新台币',
  USD = '美元',
  JPY = '日元',
  KRW = '韩元',
  GBP = '英镑',
  EUR = '欧元'
}

// 用户UI布局枚举
export enum UserUILayout {
  COMPACT = '紧凑',
  SPACIOUS = '宽松'
}

// 用户UI颜色风格枚举
export enum UserUIColorStyle {
  DARK = '深色',
  LIGHT = '浅色'
}

// 组织状态枚举
export enum OrgStatus {
  ACTIVE = 'ACTIVE',        // 活跃
  INACTIVE = 'INACTIVE',    // 非活跃
  SUSPENDED = 'SUSPENDED'   // 暂停
}

// 组织数据状态枚举
export enum OrgDataStatus {
  NORMAL = 'NORMAL',        // 正常
  DELETED = 'DELETED',      // 删除
  ARCHIVED = 'ARCHIVED'     // 归档
}

// 组织账户类型枚举
export enum OrgAccountType {
  FREE = 'FREE',                  // 免费
  SUBSCRIPTION = 'SUBSCRIPTION',  // 订阅
  BUYOUT = 'BUYOUT'              // 买断付费
}

// 组织类型枚举
export enum OrgType {
  FILM_COMPANY = 'FILM_COMPANY',     // 影视公司
  CREW = 'CREW',                     // 剧组
  PRODUCTION_TEAM = 'PRODUCTION_TEAM' // 制作团队
}

// 部门状态枚举
export enum DepartmentStatus {
  ENABLE = 'ENABLE',        // 启用
  DEACTIVATE = 'DEACTIVATE', // 停用
  DELETE = 'DELETE'         // 删除
}

// 部门用户关系状态枚举
export enum DepartmentUserStatus {
  ACTIVE = 'ACTIVE',        // 活跃
  INACTIVE = 'INACTIVE',    // 非活跃
  SUSPENDED = 'SUSPENDED'   // 暂停
}

/**
 * 用户信息类型
 */
export interface UserInfo {
  userID: string;
  userName: string;
  userPhoneCountryCode?: string; // 手机号国际区号（如：+86, +852等）
  userPhone?: string;
  userEmail?: string;
  userPassword?: string;
  userRealName?: string;
  userNickName?: string;
  userAvatar?: string;
  userPosition?: string;
  userRole?: string; // 多选，逗号分隔
  userAccountType?: UserAccountType;
  userAccountStatus?: string;
  userCountry?: string; // 国家
  userCity?: string; // 城市
  
  // 订阅相关字段 - 分场排期产品
  userSubscriptionBreakdownVersion?: UserSubscriptionType;
  userSubscriptionBreakdownStartDate?: string;
  userSubscriptionBreakdownExpirationDate?: string;
  
  // 订阅相关字段 - 预算管理产品
  userSubscriptionBudgetsVersion?: UserSubscriptionType;
  userSubscriptionBudgetsStartDate?: string;
  userSubscriptionBudgetsExpirationDate?: string;
  
  // 权限相关字段
  userManagementOrgs?: string; // 多个，逗号分隔
  userManagementDepartments?: string; // 多个，逗号分隔
  
  // 组织相关字段
  userOrgID?: string; // 多个，逗号分隔
  
  /** 应用界面配置 JSON 字符串 */
  userAppConfig?: string;

  // 用户行为记忆相关字段
  userLastUsedFilmID?: string;
  userLastUsedBudgetID?: string;
  userLastUsedBudgetItemID?: string; // 存储多个 [budgetID;budgetItemID] 的数组，使用JSON格式
  userLastUsedBreakdownID?: string;
  userLastUsedSceneID?: string; // 存储多个 [breakdownID;sceneID] 的数组，使用JSON格式
  userLastUsedElementID?: string; // 存储多个 [breakdownID;elementID] 的数组，使用JSON格式
  userLastUsedScheduleID?: string; // 存储多个 [breakdownID;scheduleID] 的数组，使用JSON格式
  userLastUsedSlotID?: string; // 存储多个 [breakdownID;scheduleID;slotID] 的数组，使用JSON格式
  userLastUsedCalDateID?: string; // 存储多个 [scheduleID;calDateID] 的数组，使用JSON格式
  userLastUsedCalendarDate?: string; // 存储多个 [scheduleID;日期] 的数组，使用JSON格式
  userLastUsedCurrency?: UserCurrency;
  
  createdAt: string;
  updatedAt: string;
}

/**
 * 组织信息类型
 */
export interface OrgInfo {
  orgID: string;
  orgName: string;
  orgStatus: OrgStatus;
  orgCreateTime: string;
  orgUpdateTime: string;
  orgDataStatus: OrgDataStatus;
  orgCountry?: string;
  orgCity?: string;
  orgAccountType: OrgAccountType;
  orgType?: OrgType;
}

/**
 * 部门信息类型
 */
export interface DepartmentInfo {
  departmentID: string;
  departmentName: string;
  departmentBelongOrgID: string;
  departmentFatherDepartmentID?: string;
  departmentLevel: number;
  departmentStatus: DepartmentStatus;
  departmentSupervisorID?: string;
  departmentCreateTime: string;
  departmentUpdateTime: string;
}

/**
 * 部门用户关系信息类型
 */
export interface DepartmentUserInfo {
  relationID: string;
  departmentID: string;
  userID: string;
  relationStatus: DepartmentUserStatus;
  userPositionInDepartment?: string;
  joinTime?: string;
  leaveTime?: string;
  relationCreateTime: string;
  relationUpdateTime: string;
  // 关联的部门信息
  department: DepartmentInfo;
}

/**
 * 完整的登录响应数据类型
 */
export interface LoginResponseData {
  // 用户基本信息
  user: UserInfo;
  
  // 用户所在组织的完整信息
  organization?: OrgInfo;
  
  // 用户关联的所有部门信息（包含部门详情）
  departments: DepartmentUserInfo[];
  
  // JWT 访问令牌
  access_token: string;
}

/**
 * 用户缓存数据类型（存储在 localStorage 中）
 */
export interface UserCacheData {
  // 用户基本信息
  user: UserInfo;
  
  // 用户所在组织信息
  organization?: OrgInfo;
  
  // 用户关联的部门信息
  departments: DepartmentUserInfo[];
  
  // 访问令牌
  accessToken: string;
  
  // 缓存时间戳
  cacheTime: string;
}

/**
 * 登录请求参数类型
 */
export interface LoginParams {
  identifier: string;
  password: string;
}

/**
 * 统一API响应格式
 */
export interface ApiResponse<T = any> {
  success: boolean;
  code: string;
  message: string;
  data?: T;
  timestamp: string;
}

/**
 * 登录响应类型
 */
export interface LoginResponse extends ApiResponse<LoginResponseData> {}
